import { useState, useEffect } from 'react';
import { Modal, Button, Stars, Prix, Badge, Spinner, Textarea, Toast, Input, Select } from '../ui/UI';
import AddressAutocomplete from '../ui/AddressAutocomplete';
import { fetchRestaurantById, addReview, deleteReview, setUserStatut, getStatutForUser, uploadPhoto, fetchPhotos, deleteRestaurant } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const CUISINE_EMOJIS = {
  'Italien': '🍕', 'Japonais': '🍣', 'Français': '🥐', 'Mexicain': '🌮',
  'Indien': '🍛', 'Chinois': '🥟', 'Américain': '🍔', 'Grec': '🫒',
  'Thaïlandais': '🍜', 'Libanais': '🧆', 'Espagnol': '🥘', 'Autre': '🍽️',
};
const CUISINES = ['Italien', 'Japonais', 'Français', 'Mexicain', 'Indien', 'Chinois', 'Américain', 'Grec', 'Thaïlandais', 'Libanais', 'Espagnol', 'Autre'];
const PRIX_OPTIONS = [{ value: 1, label: '€ — Moins de 15€' }, { value: 2, label: '€€ — 15€ à 30€' }, { value: 3, label: '€€€ — 30€ à 60€' }, { value: 4, label: '€€€€ — Plus de 60€' }];

function StatutPersonnel({ statut }) {
  if (statut === 'tester') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 50, fontSize: 12, fontWeight: 600, background: '#FEF3C7', color: '#D97706' }}>📌 À tester</span>
  );
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 50, fontSize: 12, fontWeight: 600, background: '#D1FAE5', color: '#059669' }}>✅ Testé</span>
  );
}

export default function RestaurantDetail({ restaurantId, open, onClose, onUpdate }) {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Mode édition
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (open && restaurantId) load();
    if (!open) setEditMode(false);
  }, [open, restaurantId]);

  async function load() {
    setLoading(true);
    try {
      const r = await fetchRestaurantById(restaurantId);
      setRestaurant(r);
      setPhotos(r.photos || []);
      if (user) {
        const myReview = r.reviews?.find(rv => rv.user_id === user.id);
        if (myReview) { setNote(myReview.note); setCommentaire(myReview.commentaire || ''); }
        else { setNote(0); setCommentaire(''); }
      }
    } finally {
      setLoading(false);
    }
  }

  function openEdit() {
    setEditForm({
      nom: restaurant.nom,
      adresse: restaurant.adresse,
      type_cuisine: restaurant.type_cuisine,
      niveau_prix: restaurant.niveau_prix,
      date_visite: restaurant.date_visite || '',
      lat: restaurant.lat,
      lng: restaurant.lng,
    });
    setEditMode(true);
  }

  async function handleSaveEdit() {
    if (!editForm.nom?.trim() || !editForm.adresse?.trim()) {
      setToast({ message: 'Nom et adresse obligatoires.', type: 'error' });
      return;
    }
    setEditLoading(true);
    try {
      // Regéocoder si l'adresse a changé
      let lat = editForm.lat;
      let lng = editForm.lng;
      if (editForm.adresse !== restaurant.adresse && !editForm._coordsFromSelect) {
        const { geocodeAdresse } = await import('../../lib/api');
        const coords = await geocodeAdresse(editForm.adresse);
        lat = coords?.lat || lat;
        lng = coords?.lng || lng;
      }

      const { error } = await supabase
        .from('restaurants')
        .update({
          nom: editForm.nom.trim(),
          adresse: editForm.adresse.trim(),
          type_cuisine: editForm.type_cuisine,
          niveau_prix: parseInt(editForm.niveau_prix),
          date_visite: editForm.date_visite || null,
          lat, lng,
        })
        .eq('id', restaurantId);

      if (error) throw error;
      setToast({ message: 'Restaurant modifié ! ✅', type: 'success' });
      setEditMode(false);
      await load();
      onUpdate?.();
    } catch (err) {
      setToast({ message: 'Erreur : ' + err.message, type: 'error' });
    } finally {
      setEditLoading(false);
    }
  }

  async function handleSubmitReview() {
    if (!note) return;
    setSubmitting(true);
    try {
      await addReview(restaurantId, user.id, note, commentaire);
      setToast({ message: 'Avis publié !', type: 'success' });
      await load();
      onUpdate?.();
    } catch {
      setToast({ message: 'Erreur lors de la publication.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteReview(reviewId) {
    if (!window.confirm('Supprimer votre avis ?')) return;
    await deleteReview(reviewId, restaurantId);
    setToast({ message: 'Avis supprimé.', type: 'success' });
    await load();
    onUpdate?.();
  }

  async function handleChangeStatut(newStatut) {
    await setUserStatut(restaurantId, user.id, newStatut);
    setToast({ message: 'Votre statut a été mis à jour !', type: 'success' });
    await load();
    onUpdate?.();
  }

  async function handleDeleteRestaurant() {
    if (!window.confirm(`Supprimer "${restaurant?.nom}" définitivement ? Cette action est irréversible.`)) return;
    try {
      await deleteRestaurant(restaurantId);
      setToast({ message: 'Restaurant supprimé.', type: 'success' });
      setTimeout(() => { onClose(); onUpdate?.(); }, 1200);
    } catch (err) {
      setToast({ message: 'Erreur : ' + err.message, type: 'error' });
    }
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError("Ce fichier n'est pas une image."); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Image trop lourde (max 5 Mo).'); return; }
    setUploadingPhoto(true);
    setUploadError(null);
    try {
      await uploadPhoto(file, restaurantId);
      const p = await fetchPhotos(restaurantId);
      setPhotos(p);
      setActivePhoto(p.length - 1);
      setToast({ message: 'Photo ajoutée ! 📸', type: 'success' });
    } catch (err) {
      setUploadError(err.message || 'Erreur upload. Vérifiez le bucket Supabase.');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  }

  if (!open) return null;

  const r = restaurant;
  const emoji = CUISINE_EMOJIS[r?.type_cuisine] || '🍽️';
  const myReview = r?.reviews?.find(rv => rv.user_id === user?.id);
  const createur = r?.added_by_profile?.username;
  const monStatut = user ? getStatutForUser(r || {}, user.id) : null;
  const isCreateur = user && r?.added_by_profile?.id === user.id;

  return (
    <>
      <Modal open={open} onClose={() => { setEditMode(false); onClose(); }} title="" width="680px">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner size={32} /></div>
        ) : !r ? null : editMode ? (

          /* ══════════════════════════════════════
             MODE ÉDITION
          ══════════════════════════════════════ */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <button onClick={() => setEditMode(false)} style={{ background: 'var(--surface-3)', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text-2)' }}>
                ← Retour
              </button>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>Modifier le restaurant</h3>
            </div>

            <Input
              label="Nom du restaurant *"
              value={editForm.nom || ''}
              onChange={e => setEditForm(f => ({ ...f, nom: e.target.value }))}
              icon="🍽️"
            />

            <AddressAutocomplete
              value={editForm.adresse || ''}
              onChange={val => setEditForm(f => ({ ...f, adresse: val, _coordsFromSelect: false }))}
              onSelect={s => setEditForm(f => ({ ...f, adresse: s.label, lat: s.lat, lng: s.lng, _coordsFromSelect: true }))}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Select
                label="Type de cuisine"
                value={editForm.type_cuisine || 'Autre'}
                onChange={e => setEditForm(f => ({ ...f, type_cuisine: e.target.value }))}
                options={CUISINES.map(c => ({ value: c, label: c }))}
              />
              <Select
                label="Niveau de prix"
                value={editForm.niveau_prix || 2}
                onChange={e => setEditForm(f => ({ ...f, niveau_prix: e.target.value }))}
                options={PRIX_OPTIONS.map(p => ({ value: p.value, label: p.label }))}
              />
            </div>

            <Input
              label="Date de visite (optionnel)"
              type="date"
              value={editForm.date_visite || ''}
              onChange={e => setEditForm(f => ({ ...f, date_visite: e.target.value }))}
            />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <Button variant="outline" onClick={() => setEditMode(false)}>Annuler</Button>
              <Button onClick={handleSaveEdit} loading={editLoading}>💾 Enregistrer</Button>
            </div>
          </div>

        ) : (

          /* ══════════════════════════════════════
             MODE LECTURE
          ══════════════════════════════════════ */
          <div style={{ margin: '-24px' }}>

            {/* Photo header */}
            <div style={{ height: 260, background: 'linear-gradient(135deg, #FFF0F1, #FFF5F0)', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0' }}>
              {photos.length > 0 ? (
                <>
                  <img src={photos[activePhoto]?.url} alt={r.nom} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {photos.length > 1 && (
                    <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
                      {photos.map((_, i) => (
                        <button key={i} onClick={() => setActivePhoto(i)} style={{ width: 8, height: 8, borderRadius: '50%', background: i === activePhoto ? '#fff' : 'rgba(255,255,255,0.5)', border: 'none', cursor: 'pointer', padding: 0 }} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <span style={{ fontSize: 80, opacity: 0.5 }}>{emoji}</span>
              )}

              {user && (
                <label style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.92)', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: uploadingPhoto ? 'not-allowed' : 'pointer', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  {uploadingPhoto ? <Spinner size={13} /> : '📷'}
                  {uploadingPhoto ? 'Envoi…' : 'Photo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={handlePhotoUpload} disabled={uploadingPhoto} />
                </label>
              )}
            </div>

            {uploadError && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 16px', margin: '12px 24px 0', fontSize: 13, color: '#DC2626' }}>
                ⚠️ {uploadError}
              </div>
            )}

            <div style={{ padding: '20px 24px 24px' }}>

              {/* Nom + prix + bouton modifier */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{r.nom}</h2>
                  <p style={{ color: 'var(--text-3)', fontSize: 14 }}>📍 {r.adresse}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  <Prix niveau={r.niveau_prix} />
                  {/* Bouton modifier — uniquement pour le créateur */}
                  {isCreateur && (
                    <button onClick={openEdit} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: 'var(--surface-3)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}
                    >
                      ✏️ Modifier
                    </button>
                  )}
                </div>
              </div>

              {/* Ajouté par */}
              {createur && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 50, padding: '4px 12px 4px 6px', marginBottom: 14 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, var(--brand), var(--brand-light))', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {createur[0].toUpperCase()}
                  </div>
                  <span style={{ fontSize: 13, color: 'var(--text-2)' }}>Ajouté par <strong>{createur}</strong></span>
                </div>
              )}

              {/* Tags */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                {r.type_cuisine && <Badge color="gray">{emoji} {r.type_cuisine}</Badge>}
                {r.note_moyenne && (
                  <Badge color="orange">
                    <Stars note={Math.round(r.note_moyenne)} size={12} />
                    <span style={{ marginLeft: 4 }}>{r.note_moyenne} ({r.nb_avis} avis)</span>
                  </Badge>
                )}
                {r.date_visite && <Badge color="blue">📅 {new Date(r.date_visite).toLocaleDateString('fr-FR')}</Badge>}
              </div>

              {/* Statut personnel */}
              {user && (
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <div>
                      <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Mon statut personnel</p>
                      <StatutPersonnel statut={monStatut} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="sm" variant={monStatut === 'tester' ? 'secondary' : 'outline'} onClick={() => handleChangeStatut('tester')}>📌 À tester</Button>
                      <Button size="sm" variant={monStatut === 'teste' ? 'primary' : 'outline'} onClick={() => handleChangeStatut('teste')}>✅ Testé</Button>
                    </div>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8 }}>Ce statut est uniquement visible pour vous.</p>
                </div>
              )}

              {/* Ajouter avis */}
              {user && monStatut !== 'tester' && (
                <div style={{ background: 'var(--surface-2)', borderRadius: 14, padding: '18px', marginBottom: 20 }}>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 12 }}>
                    {myReview ? '✏️ Modifier mon avis' : '✍️ Ajouter mon avis'}
                  </h4>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)', display: 'block', marginBottom: 6 }}>Ma note</label>
                    <Stars note={note} size={28} interactive onChange={setNote} />
                  </div>
                  <Textarea value={commentaire} onChange={e => setCommentaire(e.target.value)} placeholder="Partagez votre expérience…" />
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <Button onClick={handleSubmitReview} loading={submitting} disabled={!note}>
                      {myReview ? 'Mettre à jour' : 'Publier'}
                    </Button>
                    {myReview && <Button variant="danger" size="sm" onClick={() => handleDeleteReview(myReview.id)}>Supprimer</Button>}
                  </div>
                </div>
              )}

              {/* Avis */}
              <div>
                <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 14 }}>
                  💬 Avis ({r.reviews?.length || 0})
                </h4>
                {r.reviews?.length === 0 ? (
                  <p style={{ color: 'var(--text-3)', fontSize: 14 }}>Aucun avis pour l'instant.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {r.reviews?.map(rv => (
                      <div key={rv.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: rv.commentaire ? 8 : 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, var(--brand), var(--brand-light))', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
                              {rv.profiles?.username?.[0]?.toUpperCase() || '?'}
                            </div>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{rv.profiles?.username || 'Utilisateur'}</span>
                          </div>
                          <Stars note={rv.note} size={14} />
                        </div>
                        {rv.commentaire && <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6 }}>{rv.commentaire}</p>}
                        <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>{new Date(rv.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Lien Maps */}
              {r.adresse && (
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(r.nom + ' ' + r.adresse)}`} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, padding: '12px 16px', background: 'var(--surface-2)', borderRadius: 10, fontSize: 14, fontWeight: 500, color: 'var(--blue)' }}>
                  🗺️ Voir sur Google Maps
                </a>
              )}

              {/* Actions créateur : modifier + supprimer */}
              {isCreateur && (
                <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                  <button onClick={openEdit} style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '11px 16px', background: 'var(--surface-3)', border: '1px solid var(--border)',
                    borderRadius: 10, fontSize: 14, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer', transition: 'all 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}
                  >
                    ✏️ Modifier
                  </button>
                  <button onClick={handleDeleteRestaurant} style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '11px 16px', background: '#FEF2F2', border: '1px solid #FECACA',
                    borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#DC2626', cursor: 'pointer', transition: 'all 0.2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FEE2E2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#FEF2F2'; }}
                  >
                    🗑️ Supprimer
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}
